import Button from '@components/Button'
import { shortAddress } from '@plug-core/utils/shortAddress'
import { Contact } from '@plug-core/interfaces/common'
import { TextField, Typography, useTheme } from '@mui/material'
import { AddressType } from '@plug-core/utils/addressMatcher'
import React, { useMemo, useState } from 'react'
import { HiOutlineUser } from 'react-icons/hi'
import { SendMode } from '@plug-core/hooks/useSend'
import { useTranslation } from 'react-i18next'
import BottomSheet from '@components/BottomSheet'
import ContactForm from '@modules/ContactForm'
import useTopup from '@plug-core/hooks/useTopup'
import CanisterForm from '@modules/TopUp/components/CanisterForm'

interface IProps {
  toAddress: string
  isValidAddress: boolean
  setToAddress: (address: string) => void
  contacts: Contact[]
  addressType: AddressType
  sendMode: SendMode
}

export const AddressTextField = ({
  toAddress,
  isValidAddress,
  setToAddress,
  contacts,
  addressType,
  sendMode,
}: IProps) => {
  const theme = useTheme()

  const existingContact = contacts.find((c) => c.address === toAddress)
  const [contactModalVisible, setContactModalVisible] = useState(false)

  const { t } = useTranslation()
  const { addCanister } = useTopup()

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToAddress(e.target.value)
  }

  const onPaste: React.ClipboardEventHandler = (e) => {
    e.preventDefault()

    setToAddress(e.clipboardData.getData('Text').trim() || '')
  }

  const presentingValue = useMemo(() => {
    if (existingContact) return existingContact.name

    return isValidAddress ? shortAddress(toAddress, { leftSize: 12, rightSize: 3 }) : toAddress
  }, [isValidAddress, toAddress, existingContact])

  const showContactSaveButton = addressType !== AddressType.Invalid && !existingContact

  return (
    <>
      <TextField
        variant="standard"
        autoFocus
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <Typography pr={1} pb={0.25} align="center" variant="subtitle1">
              {t(`send.addressField.${sendMode}.title`)}
            </Typography>
          ),
          endAdornment: showContactSaveButton && (
            <Button
              variant="link"
              startIcon={<HiOutlineUser color={theme.palette.rainbow.blue} />}
              onClick={() => setContactModalVisible(true)}
            >
              {t('common.save')}
            </Button>
          ),
        }}
        placeholder={t(`send.addressField.${sendMode}.placeholder`)}
        inputProps={{
          style: isValidAddress ? { color: theme.palette.rainbow.blue, fontWeight: 600 } : {},
        }}
        value={presentingValue}
        onChange={handleAddressChange}
        onPaste={onPaste}
        autoComplete="off"
      />
      <BottomSheet
        visible={contactModalVisible}
        onBackdropClick={() => setContactModalVisible(false)}
      >
        {sendMode === SendMode.Send ? (
          <ContactForm address={toAddress} close={() => setContactModalVisible(false)} />
        ) : (
          <CanisterForm
            hideSelf={() => setContactModalVisible(false)}
            addCanister={addCanister}
            prefilledCanisterId={toAddress}
            newCanister
          />
        )}
      </BottomSheet>
    </>
  )
}
